import {
  Box,
  CircularProgress,
  ErrorMark,
  Field as FieldShell,
  TextField,
} from '@presov.sk/presov-ui-react';
import type { FieldValidator } from 'final-form';
import React from 'react';
import { Field } from 'react-final-form';

export type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  fullWidth?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate?: FieldValidator<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  format?(value: any, name: string): any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parse?(value: any, name: string): any;
  type?: 'text' | 'email' | 'password' | 'number';
  autoFocus?: boolean;
  endAdornment?: React.ReactNode;
  multiline?: boolean;
  rows?: number;
  info?: React.ReactNode;
  maxLength?: number;
  maxLengthIndicator?: boolean;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  markdown?: boolean;
  loading?: boolean;
  min?: number;
  max?: number;
  sx?: React.ComponentProps<typeof FieldShell>['sx'];
};

/**
 * Textové pole formulára. Rám, výšku aj typografiu drží téma; label, ⓘ,
 * povinnosť a počítadlo znakov `Field` z knižnice.
 *
 * Chyba je **červený rám a koliesko „!" v poli**, nie text pod ním: text nesie
 * tooltip pri koliesku a skrytý uzol `<id>-err`, na ktorý sa pole odkazuje cez
 * `aria-describedby`. Vďaka tomu má pole rovnakú výšku s chybou aj bez nej
 * a formulár pri prvej chybe neposkočí - predtým sa to riešilo vyhradeným
 * riadkom s jedinou medzerou.
 */
const FormTextField: React.FC<Props> = ({
  name,
  label,
  placeholder,
  autoComplete,
  required,
  fullWidth,
  validate,
  format,
  parse,
  type,
  autoFocus,
  endAdornment,
  loading = false,
  multiline = false,
  rows,
  info,
  maxLength,
  maxLengthIndicator = true,
  disabled = false,
  inputRef,
  markdown,
  min,
  max,
  sx,
}) => {
  return (
    <Field
      name={name}
      validate={validate}
      format={format}
      parse={parse}
      type={type}
    >
      {({
        input: { name, value, onChange, onBlur },
        meta: { touched, error, submitError },
      }) => {
        const errorMessage = touched ? error || submitError : submitError;
        const isError = !!errorMessage;
        const id = `id-${name}`;
        const length = String(value ?? '').length;

        return (
          <FieldShell
            sx={sx}
            label={label}
            htmlFor={id}
            required={required}
            info={info}
            error={errorMessage || null}
            counter={
              maxLengthIndicator && maxLength ? (
                // Pri dosiahnutom limite červené - inak sa nedá rozoznať, či
                // text končí, alebo ho pole odseklo.
                <Box
                  component='span'
                  sx={{ color: length >= maxLength ? 'error.main' : 'inherit' }}
                >
                  {length}/{maxLength}
                </Box>
              ) : undefined
            }
          >
            <TextField
              id={id}
              error={isError}
              markdown={markdown}
              type={type}
              name={name}
              placeholder={placeholder}
              autoComplete={autoComplete}
              fullWidth={fullWidth}
              variant='outlined'
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              autoFocus={autoFocus}
              slotProps={{
                input: {
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={16} /> : null}
                      {endAdornment}
                      {isError ? <ErrorMark message={errorMessage} /> : null}
                    </>
                  ),
                },
                htmlInput: {
                  'aria-describedby': isError ? `${id}-err` : undefined,
                  ...(maxLength && { maxLength }),
                  ...(min !== undefined && { min }),
                  ...(max !== undefined && { max }),
                },
              }}
              multiline={multiline}
              rows={rows}
              disabled={disabled}
              inputRef={inputRef}
            />
          </FieldShell>
        );
      }}
    </Field>
  );
};

export default FormTextField;
